import type { FieldConfig } from "@/admin/FieldConfig.tsx";
import { AInput } from "@/admin/form/Fields.tsx";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { WFileUploadField } from "@/components/forms/WFileUploadField.tsx";
import { WTextField, type WTextFieldProps } from "@/components/forms/WTextField.tsx";
import type { ReactNode } from "react";
import { type Control, Controller, type FieldPath, type FieldValues } from "react-hook-form";

export function AFTextField<T extends FieldValues>({
    control,
    name,
    config,
    nullable,
    children = <AInput />,
    ...props
}: {
    control: Control<T>;
    name: FieldPath<T>;
    config?: FieldConfig<any, string> | FieldConfig<any, string | undefined | null>;
    isRequired?: boolean;
    nullable?: boolean;
    children?: ReactNode;
} & Omit<WTextFieldProps, "value" | "onChange">) {
    return (
        <Controller
            control={control}
            name={name}
            {...controllerProps(props)}
            render={(f) => (
                <WTextField
                    {...formPropsToAriaProps(f)}
                    wide
                    label={getEditTitle(config, name)}
                    value={f.field.value ?? ""}
                    onChange={(v) => f.field.onChange(v === "" && nullable ? null : v)}
                    {...props}
                >
                    {children}
                </WTextField>
            )}
        />
    );
}

function getEditTitle(config: FieldConfig<any, any> | undefined, name: string): ReactNode {
    return config?.editTitle ?? config?.title ?? name;
}

export function AFCheckbox<T extends FieldValues>({
    config,
    control,
    name,
    headless,
}: {
    control: Control<T>;
    name: FieldPath<T>;
    config?: FieldConfig<any, boolean>;
    headless?: boolean;
}) {
    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => (
                <FormField wide headless={headless} label={getEditTitle(config, name)}>
                    <WCheckbox isSelected={field.value} onChange={(v) => field.onChange(v)} />
                </FormField>
            )}
        />
    );
}

export function AFFileField<T extends FieldValues>({
    control,
    name,
    config,
}: {
    control: Control<T>;
    name: FieldPath<T>;
    config?: FieldConfig<any, string> | FieldConfig<any, string | undefined | null>;
} & Omit<WTextFieldProps, "value" | "onChange">) {
    return (
        <Controller
            control={control}
            name={name}
            render={({ field }) => (
                <WFileUploadField
                    onChange={(v) => field.onChange(v)}
                    value={field.value}
                    label={getEditTitle(config, name)}
                />
            )}
        />
    );
}
